/**
 * @see https://github.com/mantinedev/mantine/blob/master/packages/@mantine/hooks/src/use-debounced-callback/use-debounced-callback.ts
 */

import * as React from 'react'

import { useCallbackRef } from '@/hooks/use-callback-ref'

type DebouncedFunction<T extends (...args: never[]) => unknown> = ((
  ...args: Parameters<T>
) => void) & {
  cancel: () => void
  /**
   * Fire the latest queued callback immediately (and clear the timer).
   * No-op when no call is pending. Useful for `visibilitychange` /
   * `pagehide` listeners that need to commit pending writes before the
   * tab is suspended or torn down.
   */
  flush: () => void
}

export function useDebouncedCallback<T extends (...args: never[]) => unknown>(
  callback: T,
  delay: number
): DebouncedFunction<T> {
  const handleCallback = useCallbackRef(callback)
  const debounceTimerRef = React.useRef(0)
  const pendingArgsRef = React.useRef<Parameters<T> | null>(null)
  React.useEffect(() => () => window.clearTimeout(debounceTimerRef.current), [])

  const cancel = React.useCallback(() => {
    window.clearTimeout(debounceTimerRef.current)
    pendingArgsRef.current = null
  }, [])

  const flush = React.useCallback(() => {
    if (!pendingArgsRef.current) {
      return
    }
    window.clearTimeout(debounceTimerRef.current)
    const args = pendingArgsRef.current
    pendingArgsRef.current = null
    handleCallback(...args)
  }, [handleCallback])

  const setValue = React.useCallback(
    (...args: Parameters<T>) => {
      window.clearTimeout(debounceTimerRef.current)
      pendingArgsRef.current = args
      debounceTimerRef.current = window.setTimeout(() => {
        pendingArgsRef.current = null
        handleCallback(...args)
      }, delay)
    },
    [handleCallback, delay]
  )

  return React.useMemo(
    () => Object.assign(setValue, { cancel, flush }) as DebouncedFunction<T>,
    [setValue, cancel, flush]
  )
}
